// v4 Task #11 — Project Status Update workflow tab.
//
// Per-project tab showing every currently-marked Item + Action (the
// "draft preview" surface — Section 5.7). The .docx generation
// pipeline lands in Task #12; this tab is the mark management +
// preview view.
//
// Mark toggling reuses the v4 correction kinds from Task #8/9 wired
// through the existing /feedback/:runId path. Bulk "Select all" on
// an Item header iterates the kil_mark POST per child Action — no
// new server-side endpoint needed.

import {
  ApiError, downloadFile, getJson, postJson, wireDownloadControl,
} from "./api";
import {
  PHASE_ORDER,
  phaseBand,
  renderBreadcrumb,
  type BreadcrumbView,
  type PhaseValue,
} from "./briefings";
import { inFixtureMode } from "./fixture";
import { workItemKey } from "./target_id";
import { showUndoToast } from "./undo_toast";

// Decision 5.4 bulk-mark shortcut ("Select all child Actions" on the KIL
// tab, "Mark all (N) for KIL" on the briefing Item header). Hidden by
// default per operator request — the render is distracting before
// clients have asked for it. Flip both flags to true to restore.
// The implementation, CSS, and 14 vitest cases all stay intact.
const BULK_KIL_MARK_VISIBLE = false;

// v4 Task #12 — generation archive entries. Listing returns metadata
// only (no blobs); the SPA renders a Download link that hits
// /kil-generations/:id/download for the bytes.
export interface KilGenerationListItem {
  id: string;
  generated_at: string;
  generated_by: string;
  week_end: string | null;
  filename: string;
  label: string | null | undefined;
  marked_items?: number;
  marked_actions?: number;
  // #111b — which download format(s) this generation carries. The single
  // download icon serves Excel when present, else Word (historical records).
  has_xlsx?: boolean;
  has_docx?: boolean;
}

export interface KilGenerationsResponse {
  project_slug: string;
  generations: KilGenerationListItem[];
}

export async function fetchKilGenerations(): Promise<KilGenerationsResponse> {
  return getJson<KilGenerationsResponse>("/kil-generations");
}

export interface KilGenerationCreateResponse {
  id: string;
  generated_at: string;
  filename: string;
  label: string | null | undefined;
}

export async function createKilGeneration(
  filename: string, label: string | null,
): Promise<KilGenerationCreateResponse> {
  return postJson<KilGenerationCreateResponse>("/kil-generations", {
    filename, label,
  });
}

export function kilGenerationDownloadUrl(id: string): string {
  // Resolved against the same Worker origin the SPA already uses for
  // /briefings/:id/download — the api.ts wrapper prepends WORKER_URL.
  return `/kil-generations/${encodeURIComponent(id)}/download`;
}

export interface KilAction {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  ai_slug: string;
  serial: number | null;
  display_number: string | null;
  text: string;
  narrative: string | null;
  priority_status: string;
  status: "open" | "closed";
  assignee: string | null;
  due: string | null;
  first_seen: string | null;
  closed_at: string | null;
  reason: string | null;
  impact: string | null;
  off_hold_condition: string | null;
  kil_marked: boolean;
  action_type: string;
  // NOTE: `open_questions_v4` is intentionally absent. The agent stopped
  // emitting it into the marks snapshot on 2026-08-12 (client: no questions on
  // the PSU), and the field is not read anywhere in this view. Keeping the
  // declaration would invite a future renderer to reach for it and silently
  // resurrect the behaviour against blobs published before the change.
}

export interface KilItem {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  slug: string;
  serial: number | null;
  display_number: string | null;
  name: string;
  narrative: string | null;
  summary: string | null;
  status: "open" | "closed";
  thread_slug: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  // #37/#2: the Category's phase band, for PSU phase-band grouping
  // (optional — absent on snapshots published before phases shipped;
  // null/absent => General band, mirroring the briefing).
  phase?: PhaseValue | null;
  // #4: overlay sub-category grouping (optional — absent on snapshots
  // published before the carry-into-PSU change).
  subcategory_slug?: string | null;
  subcategory_name?: string | null;
  // SPA report-parity (T1 follow-up, 2026-10-28): the Item's association band
  // slugs — the UNION of its non-closed Actions' association_tags, computed
  // agent-side (`_build_kil_marks_snapshot`, xlsx + now the web push). Absent on a
  // flat project or a pre-T1 snapshot (→ no association tier); empty == banded
  // under "(No association)".
  association?: string[];
  first_seen: string | null;
  start_date: string | null;
  closed_at: string | null;
  responsible_party: {
    name: string;
    email: string | null;
    source: string | null;
    locked_at: string | null;
  } | null;
  action_items: KilAction[];
}

export interface KilCounts {
  marked_items: number;
  marked_actions: number;
  context_items: number;
}

export interface KilMarksSnapshot {
  project_slug: string;
  snapshot_at: string | null;
  items: KilItem[];
  counts?: KilCounts;
  /** Key Issue Log (558) rows closed inside the PSU's 1-week window — read by
   *  the Key Issue Log report page (report_marks.mergeClosedKilRows), not here. */
  key_issue_log?: { closed_actions?: import("./report_marks").KilClosedActionRow[] };
  /** SPA report-parity (T1 follow-up, 2026-10-28): the ordered association bands
   *  + the project executive summary — carried on the web snapshot so the PSU page
   *  mirrors the generated .xlsx (Exec Summary → Association → sub-category). Both
   *  absent on a flat project / a pre-T1 snapshot (→ no association tier, no exec
   *  block). The Worker snapshot schema is passthrough, so these ride without a
   *  Worker release. */
  associations?: Array<{ slug: string; name: string }>;
  executive_summary?: string | null;
}

export async function fetchKilMarks(): Promise<KilMarksSnapshot> {
  return getJson<KilMarksSnapshot>("/kil-marks");
}

interface BriefingsListResponse {
  briefings: Array<{ run_id: string; week_end: string }>;
}

async function latestRunId(): Promise<string | null> {
  const list = await getJson<BriefingsListResponse>("/briefings");
  return list.briefings[0]?.run_id ?? null;
}

export interface BriefingsAnchor {
  runId: string | null;
  weekEnd: string | null;
  projectSlug: string;
}

async function fetchAnchor(snapshot: KilMarksSnapshot): Promise<BriefingsAnchor> {
  // Soft-fail: in fixture mode the SPA serves from a static server that
  // doesn't expose /briefings, so getJson throws. Fall back to nulls so
  // the Generate flow still renders the form with a "draft" default
  // filename — operator can edit at generation time per Decision 5.9.
  try {
    const list = await getJson<BriefingsListResponse>("/briefings");
    const first = list.briefings[0];
    return {
      runId: first?.run_id ?? null,
      weekEnd: first?.week_end ?? null,
      projectSlug: snapshot.project_slug,
    };
  } catch {
    return {
      runId: null,
      weekEnd: null,
      projectSlug: snapshot.project_slug,
    };
  }
}

// Post an Action mark toggle. Uses the existing correction-kind
// contract: action_kil_mark / action_kil_unmark — target type
// "action_item" (id encoded as
// `thread:<t>/work_item:<wi>/action_item:<ai>`).
// #58 (PSU = Actions only): there is no Item-level mark toggle — an
// Item enters the Project Status Update solely via a marked Action,
// which auto-pulls the parent Item + Category into the snapshot.
export async function toggleActionMark(
  threadSlug: string | null,
  workItemSlug: string,
  aiSlug: string,
  marked: boolean,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) {
    throw new Error("No briefing run to anchor the mark event against.");
  }
  // Target id mirrors portal/src/target_id.ts conventions.
  const targetId = threadSlug
    ? `thread:${threadSlug}/work_item:${workItemSlug}/action_item:${aiSlug}`
    : `unthreaded/work_item:${workItemSlug}/action_item:${aiSlug}`;
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: marked ? "action_kil_mark" : "action_kil_unmark",
    target: { type: "action_item", id: targetId },
    payload: {},
  });
}

// SPA report-parity (T1 follow-up, 2026-10-28): the Item-tier PSU toggle. The PSU
// is item-keyed (`work_items.kil_marked`, client 2026-09-16), so the per-row ✓
// remove on this page un-marks the ITEM via `item_kil_unmark` (the wire kind is
// live end-to-end: schemas.ts, kil_mutate.ts, agent apply). Undo re-marks it.
// Fixture mode has no Worker — the caller updates the row optimistically.
export async function toggleItemMark(
  threadSlug: string | null,
  workItemSlug: string,
  marked: boolean,
): Promise<void> {
  if (inFixtureMode()) return; // no Worker — the caller updates the row optimistically
  const runId = await latestRunId();
  if (!runId) {
    throw new Error("No briefing run to anchor the mark event against.");
  }
  const targetId = threadSlug
    ? workItemKey(threadSlug, workItemSlug)
    : `unthreaded/work_item:${workItemSlug}`;
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: marked ? "item_kil_mark" : "item_kil_unmark",
    target: { type: "work_item", id: targetId },
    payload: {},
  });
}

// --- Default filename per Decision 5.9 -------------------------------------
//   `YYYY.MMDD ProjectDisplayName-PSU.xlsx` (#181-C2, client 2026-07-21: the
//   client-facing name is "Project Status Update" — the internal "Key Issues"
//   name is retired on client surfaces per Decision 8.2. #111b — Excel is the
//   format going forward; the download handler swaps the extension to match
//   whatever the record actually serves, so historical Word records still
//   download .docx.)
//   date = period-end of the briefing run (NOT generation timestamp)
export function defaultGeneratedFilename(
  weekEnd: string | null, projectName: string,
): string {
  const safeName = projectName.replace(/[^A-Za-z0-9_-]/g, "");
  const dateStr = weekEnd ? weekEnd.replace(/-/g, "").replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2$3") : "draft";
  return `${dateStr} ${safeName}-PSU.xlsx`;
}

// --- PSU grouping (Association → sub-category → Item) ------------------------
// SPA report-parity (T1 follow-up, 2026-10-28) — the SPA mirror of the generated
// .xlsx grouping (agent/psu_xlsx.py _banded + _subcat_groups): Association band →
// sub-category → Item. An Item renders once under EACH of its associations (O1);
// an empty-union Item goes to a trailing "(No association)" band — never dropped
// (the PSU shows every marked Item, unlike the 558's N2 exclusion). A flat /
// no-axis project (no `associations`) yields a single band with NO association
// header. Sub-categories render in first-encounter order; an un-sub-categorised
// Item falls in the "" bucket (flat, no header). The phase band + numbered
// Category tier are DROPPED (Ray 2026-10-27 restyle).

export interface PsuSubcatGroup { slug: string; name: string; items: KilItem[] }
export interface PsuBand { name: string | null; subcats: PsuSubcatGroup[] }

function psuSubcatGroups(items: KilItem[]): PsuSubcatGroup[] {
  const groups = new Map<string, PsuSubcatGroup>();
  for (const it of items) {
    const slug = it.subcategory_slug ?? "";
    const g = groups.get(slug)
      ?? { slug, name: (it.subcategory_name ?? "").trim(), items: [] };
    g.items.push(it);
    groups.set(slug, g);
  }
  return [...groups.values()];
}

export function groupKilItemsForPsu(
  items: KilItem[],
  bands: Array<{ slug: string; name: string }>,
): PsuBand[] {
  if (bands.length === 0) {
    return [{ name: null, subcats: psuSubcatGroups(items) }];
  }
  const buckets = new Map<string, KilItem[]>();
  for (const b of bands) buckets.set(b.slug, []);
  const noAssoc: KilItem[] = [];
  for (const it of items) {
    let placed = false;
    for (const s of it.association ?? []) {
      if (buckets.has(s)) { buckets.get(s)!.push(it); placed = true; }
    }
    if (!placed) noAssoc.push(it);
  }
  const out: PsuBand[] = [];
  for (const b of bands) {
    const list = buckets.get(b.slug)!;
    if (list.length > 0) out.push({ name: b.name, subcats: psuSubcatGroups(list) });
  }
  if (noAssoc.length > 0) {
    out.push({ name: "(No association)", subcats: psuSubcatGroups(noAssoc) });
  }
  return out;
}

/** The project executive summary for the S1 leading block. Coerced defensively —
 *  a blob value can arrive as a raw non-string on an edge path (mirrors
 *  agent/psu_xlsx._exec_text). */
function psuExecText(snapshot: KilMarksSnapshot): string {
  const v = snapshot.executive_summary;
  return typeof v === "string" ? v.trim() : "";
}

// --- Renderer ---------------------------------------------------------------

export function renderKilMarks(
  root: HTMLElement,
  snapshot: KilMarksSnapshot,
  projectName: string,
  generations: KilGenerationListItem[],
  onMutated: () => void | Promise<void>,
): void {
  root.innerHTML = "";

  // Decision 12.12 (2026-05-28, client feedback): KIL view adopts the
  // briefing page's layout — left rail (Past Generations card) +
  // main content (header + Generate widget + marked items). Mirrors
  // the briefing page's .layout/.left-rail/.viewer structure so the
  // two tabs feel like peers, not cousins.
  //
  // The layout grid only kicks in when there's a left-rail card to
  // place there (generations.length > 0). Otherwise the main content
  // renders without the grid so we don't reserve a 210px empty
  // column. The `.kil-layout` class is the discriminator.
  // The left rail is now ALWAYS present — it hosts the Generate action at its
  // top (operator placement, 2026-06-27), with the Past Generations card below
  // it when any generations exist. The Generate button itself is built further
  // down (it needs the snapshot/anchor) and inserted at the rail's top there.
  const layout = document.createElement("div");
  layout.className = "layout kil-layout";

  const leftRail = document.createElement("div");
  leftRail.className = "left-rail";
  if (generations.length > 0) {
    const genCard = document.createElement("aside");
    genCard.className = "sidebar kil-generations-sidebar";
    renderGenerationsSection(genCard, generations);
    leftRail.appendChild(genCard);
  }
  layout.appendChild(leftRail);

  // --- Main content (replaces the old top-level <main> body) ---
  const main = document.createElement("div");
  main.className = "kil-marks-view viewer";

  const heading = document.createElement("h2");
  heading.textContent = "Project Status Update";
  main.appendChild(heading);

  const subtitle = document.createElement("p");
  subtitle.className = "kil-marks-subtitle";
  const counts = snapshot.counts ?? {
    marked_items: 0, marked_actions: 0, context_items: 0,
  };
  if (snapshot.snapshot_at) {
    subtitle.textContent =
      `Draft preview — ${counts.marked_items} marked Item${counts.marked_items === 1 ? "" : "s"}, ` +
      `${counts.marked_actions} marked Action${counts.marked_actions === 1 ? "" : "s"}. ` +
      `Snapshot ${snapshot.snapshot_at}.`;
  } else {
    subtitle.textContent = "Draft preview — no marks yet.";
  }
  main.appendChild(subtitle);

  // Generate button — clicks expand an inline form (filename + label)
  // per Decision 5.9 ("operator can edit the filename at generation
  // time"). On submit: POST to /kil-generations, trigger an
  // auto-download via a programmatic anchor click (Decision 12.13,
  // 2026-05-28 — was "navigate to Past Generations and click
  // Download" which required a page reload; client flagged the
  // disconnect), then refresh in place via onMutated().
  const generateRow = document.createElement("div");
  generateRow.className = "kil-marks-generate";
  const generateBtn = document.createElement("button");
  generateBtn.type = "button";
  generateBtn.className = "kil-generate-btn";
  generateBtn.textContent = "Generate Project Status Update";
  generateBtn.disabled = snapshot.items.length === 0;
  generateBtn.addEventListener("click", async () => {
    const anchor = await fetchAnchor(snapshot);
    const defaultFilename = defaultGeneratedFilename(anchor.weekEnd, projectName);
    renderGenerateForm(generateRow, defaultFilename, async (filename, label) => {
      try {
        const result = await createKilGeneration(filename, label);
        // Decision 12.13: auto-download the freshly archived doc. MUST go
        // through the authenticated fetch layer (downloadFile) — a bare anchor
        // can't send X-Project-Slug and 400s post-#94 (see api.ts). The Worker
        // defaults to Excel when no ?format is given.
        // The download is decoupled from the list refresh: the generation is
        // archived regardless of whether the browser download starts, so a
        // download error must NOT hide the new row or read as "Generation
        // failed" (the row's Download link is the fallback).
        try {
          await downloadFile(
            kilGenerationDownloadUrl(result.id), result.filename,
          );
        } catch {
          // Generation succeeded; only the auto-download failed — non-fatal.
        }
        await onMutated();
      } catch (e) {
        const err = document.createElement("p");
        err.className = "closed-item-error";
        // Surface specific error reasons when the Worker returns
        // structured errors. The most common: `no-rendered-doc` —
        // returned when no kil-doc/<slug>/latest exists in KV yet.
        // The agent populates that key during cron's stage_synthesize
        // via publish_kil_doc_snapshot; if marks were added between
        // cron runs (via the SPA pencil → Worker snapshot mutation),
        // the kil-doc binary lags until the next cron renders it
        // from the absorbed state.db.
        if (e instanceof ApiError && e.status === 409) {
          // Staleness gate: a mark OR a content edit (narrative/title/due) landed
          // after the last render, so the Worker kicked an async re-render rather
          // than archive a stale xlsx. Show it as an informational note (not a red
          // error) and let the recipient retry once the fresh doc lands. Render the
          // Worker's `detail` (cause-accurate for both the mark and content-edit
          // cases — PSU-STALENESS 2026-10-30); the hardcoded fallback stays
          // cause-neutral rather than claiming "a mark changed" on an edit.
          const body = (e.body as { error?: string; detail?: string } | null) ?? {};
          if (body.error === "stale-regenerating") {
            err.className = "kil-marks-subtitle";  // muted/info, not error red
            err.textContent = body.detail
              ?? "Your latest changes aren't in the rendered Project Status " +
                 "Update yet — it's regenerating now. Click Generate again in a " +
                 "few seconds.";
          } else {
            err.textContent = `Generation failed: ${body.error ?? "409"}`;
          }
        } else if (e instanceof ApiError && e.status === 404) {
          const body = (e.body as { error?: string } | null) ?? {};
          if (body.error === "no-rendered-doc") {
            err.textContent =
              "No KIL .docx rendered yet. The pre-rendered doc is " +
              "refreshed each cron run from state.db marks. Marks " +
              "added in this session via the pencil are stored but " +
              "won't appear in a .docx until the next Monday cron " +
              "absorbs them (or a workflow_dispatch run completes " +
              "with the marks present in state.db).";
          } else {
            err.textContent = `Generation failed: ${body.error ?? "404"}`;
          }
        } else {
          err.textContent = `Generation failed: ${e instanceof Error ? e.message : String(e)}`;
        }
        generateRow.appendChild(err);
      }
    });
  });
  generateRow.appendChild(generateBtn);
  // Top of the left rail, above the Past Generations card (operator placement).
  leftRail.insertBefore(generateRow, leftRail.firstChild);

  if (snapshot.items.length === 0) {
    const empty = document.createElement("p");
    empty.className = "kil-marks-empty";
    empty.textContent = "No Items marked yet. Use the PSU toggle on an Item in the briefing view to add it here.";
    main.appendChild(empty);
    layout.appendChild(main);
    root.appendChild(layout);
    return;
  }

  // SPA report-parity (T1 follow-up, 2026-10-28): the page mirrors the generated
  // .xlsx (psu_xlsx.py) — Executive Summary leading block → Association band →
  // sub-category → Item (name + status narrative) → trailing "Progress Photos:"
  // row. The phase band + numbered Category tier are dropped (Ray 2026-10-27).

  // S1 — Executive Summary leading block.
  const execText = psuExecText(snapshot);
  if (execText) {
    const execBlock = document.createElement("section");
    execBlock.className = "psu-exec-summary";
    const execLabel = document.createElement("h3");
    execLabel.className = "psu-exec-summary-label";
    execLabel.textContent = "Executive Summary";
    execBlock.appendChild(execLabel);
    const execProse = document.createElement("p");
    execProse.className = "psu-exec-summary-prose";
    execProse.textContent = execText;
    execBlock.appendChild(execProse);
    main.appendChild(execBlock);
  }

  // Association → sub-category grouping. Insertion order within a band = snapshot
  // order = numeric display_number order (#180), so Items read top→bottom by number.
  for (const band of groupKilItemsForPsu(snapshot.items, snapshot.associations ?? [])) {
    const bandSection = document.createElement("section");
    bandSection.className = "psu-assoc-band";
    if (band.name !== null) {
      const bandHeader = document.createElement("h2");
      bandHeader.className = "psu-assoc-band-header";
      bandHeader.textContent = band.name;
      bandSection.appendChild(bandHeader);
    }
    for (const subcat of band.subcats) {
      const scSection = document.createElement("section");
      scSection.className = "psu-subcat-group";
      // Sub-category / topic header — only when the Item carries one; an
      // un-sub-categorised Item renders directly under its band.
      if (subcat.name) {
        const scHeader = document.createElement("h3");
        scHeader.className = "psu-subcat-header";
        scHeader.textContent = subcat.name;
        scSection.appendChild(scHeader);
      }
      for (const item of subcat.items) {
        scSection.appendChild(renderPsuItemCard(item));
      }
      bandSection.appendChild(scSection);
    }
    main.appendChild(bandSection);
  }

  // Trailing "Progress Photos:" landing row (Ray's binary, A61) — a labelled
  // section the recipient drops progress images under on the generated file.
  const photos = document.createElement("div");
  photos.className = "psu-progress-photos";
  photos.textContent = "Progress Photos:";
  main.appendChild(photos);

  layout.appendChild(main);
  root.appendChild(layout);
}

// One PSU Item card (T1 report-parity, 2026-10-28) — Item name + status narrative
// (the .xlsx's Topic | Item two-column row), a closed badge / move breadcrumb when
// present, and the per-Item ✓ remove control (un-mark via `item_kil_unmark`, with
// an undo toast). The PSU is item-only (no action rows); the 558/Key Issue Log
// carries the action detail.
function renderPsuItemCard(
  item: KilItem,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "kil-item-card psu-item-card";
  card.dataset.slug = item.slug;
  if (item.status === "closed") card.classList.add("kil-item-closed");

  const header = document.createElement("header");
  header.className = "kil-item-header";

  const title = document.createElement("h4");
  const path = item.display_number ? `${item.display_number} ` : "";
  title.textContent = `${path}${item.name}`;
  // The number above can have CHANGED since the recipient last saw this row;
  // without the crumb this view contradicts the briefing (ruling 2026-08-10).
  if (item.breadcrumb) title.appendChild(renderBreadcrumb(item.breadcrumb));
  header.appendChild(title);

  if (item.status === "closed") {
    const closedBadge = document.createElement("span");
    closedBadge.className = "kil-item-badge kil-item-badge-closed";
    closedBadge.textContent = item.closed_at ? `Closed · ${item.closed_at}` : "Closed";
    header.appendChild(closedBadge);
  }

  // The ✓ remove control (operator requirement — the per-row check toggle is
  // kept). Un-marks the Item from the PSU; an undo toast re-marks it. Appended
  // last so it sits at the row's right edge (the briefing pencil position).
  const removeBtn = document.createElement("button");
  removeBtn.type = "button";
  removeBtn.className = "kil-mark active psu-mark";
  removeBtn.textContent = "✓";
  removeBtn.title = "Remove this Item from the Project Status Update";
  removeBtn.setAttribute("aria-label", removeBtn.title);
  removeBtn.addEventListener("click", async () => {
    removeBtn.disabled = true;
    try {
      await toggleItemMark(item.thread_slug, item.slug, false);
    } catch (e) {
      removeBtn.disabled = false;
      console.error("PSU item un-mark failed:", e);
      let errEl = card.querySelector<HTMLElement>(".report-item-error");
      if (!errEl) {
        errEl = document.createElement("p");
        errEl.className = "report-item-error closed-item-error";
        card.appendChild(errEl);
      }
      errEl.textContent = "Couldn't update this placement — try again.";
      return;
    }
    // Optimistic: the mark survives on the snapshot until the next push, so
    // collapse the card now but keep it so an Undo can restore it without a
    // confirm. The toast re-marks on Undo, or finalizes the removal on expiry.
    card.classList.add("report-item-removed");
    showUndoToast({
      message: "Removed from the Project Status Update",
      onUndo: async () => {
        await toggleItemMark(item.thread_slug, item.slug, true);
        card.classList.remove("report-item-removed");
        removeBtn.disabled = false;
      },
      onExpire: () => {
        const group = card.closest<HTMLElement>(".psu-subcat-group");
        const band = card.closest<HTMLElement>(".psu-assoc-band");
        card.remove();
        if (group && !group.querySelector(".kil-item-card")) group.remove();
        if (band && !band.querySelector(".kil-item-card")) band.remove();
      },
    });
  });
  header.appendChild(removeBtn);
  card.appendChild(header);

  if (item.narrative) {
    const narrative = document.createElement("p");
    narrative.className = "kil-item-narrative";
    narrative.textContent = item.narrative;
    card.appendChild(narrative);
  }

  return card;
}

// #37/#2: partition the Category groups into phase bands for the PSU.
// `threadGroups` is the list of per-Category Item arrays in display order.
// Returns null when phasing is INACTIVE (no Category carries a non-null,
// non-'general' phase) — the caller then renders flat, byte-identical to
// the pre-phase PSU, mirroring the briefing's "bands only when active"
// rule. Otherwise returns the non-empty bands in PHASE_ORDER, each with
// its Category groups in their original (display-number) order. Only
// non-empty bands are emitted: the briefing always shows all 4 (it is the
// full project structure), but the PSU is the marked subset, where an
// empty band header would misread as "no work in this phase" rather than
// "no marked Actions here". A Category's phase is constant across its
// Items (phase is a Category attribute), so band = the first Item's band.
export function partitionKilByPhase(
  threadGroups: KilItem[][],
): Array<[PhaseValue, KilItem[][]]> | null {
  const phasingActive = threadGroups.some(
    (items) => phaseBand(items[0]?.phase) !== "general",
  );
  if (!phasingActive) return null;
  const byBand = new Map<PhaseValue, KilItem[][]>();
  for (const items of threadGroups) {
    const band = phaseBand(items[0]?.phase);
    byBand.set(band, [...(byBand.get(band) ?? []), items]);
  }
  const out: Array<[PhaseValue, KilItem[][]]> = [];
  for (const band of PHASE_ORDER) {
    const inBand = byBand.get(band);
    if (inBand && inBand.length > 0) out.push([band, inBand]);
  }
  return out;
}

// --- Generate form ---------------------------------------------------------

function renderGenerateForm(
  container: HTMLElement,
  defaultFilename: string,
  onSubmit: (filename: string, label: string | null) => Promise<void>,
): void {
  // Replace the Generate button with an inline form. Filename is pre-
  // filled per Decision 5.9; label is optional and free-text.
  container.innerHTML = "";
  const form = document.createElement("form");
  form.className = "kil-generate-form";

  const filenameLabel = document.createElement("label");
  filenameLabel.className = "kil-generate-field";
  const filenameLabelText = document.createElement("span");
  filenameLabelText.textContent = "Filename";
  const filenameInput = document.createElement("input");
  filenameInput.type = "text";
  filenameInput.value = defaultFilename;
  filenameInput.required = true;
  filenameInput.maxLength = 255;
  filenameLabel.appendChild(filenameLabelText);
  filenameLabel.appendChild(filenameInput);
  form.appendChild(filenameLabel);

  const labelLabel = document.createElement("label");
  labelLabel.className = "kil-generate-field";
  const labelLabelText = document.createElement("span");
  labelLabelText.textContent = "Label (optional)";
  const labelInput = document.createElement("input");
  labelInput.type = "text";
  labelInput.placeholder = "e.g. for May board mtg";
  labelInput.maxLength = 200;
  labelLabel.appendChild(labelLabelText);
  labelLabel.appendChild(labelInput);
  form.appendChild(labelLabel);

  const buttons = document.createElement("div");
  buttons.className = "kil-generate-form-buttons";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "kil-generate-btn";
  submit.textContent = "Submit and Download";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "kil-generate-cancel";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => {
    // Restore the original "Generate" button. Re-rendering via the
    // parent view's onMutated would refetch unnecessarily; toggling
    // the button-in-place is cheaper.
    container.innerHTML = "";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "kil-generate-btn";
    btn.textContent = "Generate Project Status Update";
    btn.addEventListener("click", () => {
      renderGenerateForm(container, defaultFilename, onSubmit);
    });
    container.appendChild(btn);
  });
  buttons.appendChild(submit);
  buttons.appendChild(cancel);
  form.appendChild(buttons);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    submit.textContent = "Creating…";
    cancel.disabled = true;
    const fname = filenameInput.value.trim();
    const label = labelInput.value.trim() || null;
    if (!fname) {
      submit.disabled = false;
      submit.textContent = "Submit and Download";
      cancel.disabled = false;
      filenameInput.focus();
      return;
    }
    await onSubmit(fname, label);
  });

  container.appendChild(form);
  filenameInput.focus();
  filenameInput.select();
}

// --- Generations archive list ---------------------------------------------

// Decision 12.14 (2026-05-28, operator follow-up): generated-logs
// card is intentionally minimal — one row per generation showing
// only the filename + a small download icon. Hovering the row
// surfaces every other detail (label, generated date/by, week
// ending, item/action counts) via a native `title` tooltip. The
// rail width is too tight for an info-dense layout; collapsing
// to filename + icon keeps the card scannable.
function renderGenerationsSection(
  parent: HTMLElement,
  generations: KilGenerationListItem[],
): void {
  const header = document.createElement("h2");
  header.textContent = `Generated Logs (${generations.length})`;
  parent.appendChild(header);

  const list = document.createElement("ul");
  list.className = "kil-generations-list";
  for (const gen of generations) {
    const li = document.createElement("li");
    li.className = "kil-generation-row";

    // Build the hover-tooltip text from the same metadata the
    // previous expanded layout rendered visibly. `title` attributes
    // get separator-aware line wrapping in most browsers; falling
    // back to ` · ` keeps it readable on one or two lines.
    const tipParts: string[] = [];
    if (gen.label) tipParts.push(gen.label);
    tipParts.push(`Generated ${formatGeneratedAt(gen.generated_at)}`);
    tipParts.push(`by ${gen.generated_by}`);
    if (gen.week_end) tipParts.push(`week ending ${gen.week_end}`);
    if (typeof gen.marked_items === "number") {
      tipParts.push(
        `${gen.marked_items} item${gen.marked_items === 1 ? "" : "s"}`,
      );
    }
    if (typeof gen.marked_actions === "number") {
      tipParts.push(
        `${gen.marked_actions} action${gen.marked_actions === 1 ? "" : "s"}`,
      );
    }
    const tipText = tipParts.join("\n");

    const fname = document.createElement("span");
    fname.className = "kil-generation-filename";
    fname.textContent = gen.filename;
    fname.title = tipText;
    li.appendChild(fname);

    // #111b — a SINGLE download icon serving whichever format this generation
    // carries: Excel when present (new records), else Word (historical records).
    const asXlsx = !!gen.has_xlsx;
    if (asXlsx || gen.has_docx) {
      const stem = gen.filename.replace(/\.(docx|xlsx)$/i, "");
      const fmtName = stem + (asXlsx ? ".xlsx" : ".docx");
      const fmtLabel = asXlsx ? "Excel" : "Word";
      const path = kilGenerationDownloadUrl(gen.id) + (asXlsx ? "?format=xlsx" : "");
      const dl = document.createElement("a");
      dl.className = "kil-generation-download";
      // Download through the authenticated fetch layer, NOT a bare href — a
      // bare-anchor GET can't send X-Project-Slug and 400s post-#94 (api.ts).
      dl.href = "#";
      dl.setAttribute("role", "button");
      dl.setAttribute("aria-label", `Download ${fmtName} (${fmtLabel})`);
      dl.title = `Download ${fmtName} (${fmtLabel})`;
      wireDownloadControl(dl, path, fmtName, (msg) => {
        let errEl = li.querySelector<HTMLElement>(".kil-generation-error");
        if (!errEl) {
          errEl = document.createElement("div");
          errEl.className = "kil-generation-error closed-item-error";
          li.appendChild(errEl);
        }
        errEl.textContent = msg;
      });
      // Same 16x16 download glyph the shell-header uses for briefing downloads
      // (see app.ts buildDownloadIcon) — consistent affordance across surfaces.
      dl.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
        '<polyline points="7 10 12 15 17 10"/>' +
        '<line x1="12" y1="15" x2="12" y2="3"/>' +
        "</svg>";
      li.appendChild(dl);
    }

    list.appendChild(li);
  }
  parent.appendChild(list);
}

export function formatGeneratedAt(iso: string): string {
  // Display as `YYYY-MM-DD HH:MM` (local). Falls back to the raw ISO
  // when parsing fails — safer than throwing on malformed inputs.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}
